import React from 'react'
import { compact, noop } from 'lodash'

import css from './style.scss'

let uid = 0

const sameValue = (a, b) => a && b && a.value === b.value

/**
 * Accessible combobox (WAI-ARIA 1.2 editable combobox with list autocomplete).
 * Options and values are `{ label, value }` objects; `value` is an array when `multi`.
 */
export default class Combobox extends React.Component {
  constructor (props) {
    super(props)
    this.id = props.id || `combobox-${++uid}`
    this.state = {
      // null while the user isn't typing, so a single select shows its selected label
      inputValue: null,
      isOpen: false,
      highlightedIndex: 0
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleBlur = this.handleBlur.bind(this)
    this.open = this.open.bind(this)
    this.clear = this.clear.bind(this)
  }

  selectedOptions () {
    const { value, multi } = this.props
    if (multi) return value || []
    return value ? [value] : []
  }

  visibleOptions () {
    const { options, multi, allowCustom } = this.props
    const query = (this.state.inputValue || '').trim().toLowerCase()
    const selected = this.selectedOptions()

    const visible = options.filter((option) =>
      !(multi && selected.some((s) => sameValue(s, option))) &&
      String(option.label).toLowerCase().indexOf(query) !== -1
    )

    if (allowCustom && query && !options.some((o) => String(o.label).toLowerCase() === query)) {
      const label = this.state.inputValue.trim()
      visible.push({ label, value: label, isNew: true })
    }

    return visible
  }

  optionId (index) {
    return `${this.id}-option-${index}`
  }

  open () {
    if (!this.props.disabled) this.setState({ isOpen: true })
  }

  close () {
    this.setState({ isOpen: false, inputValue: null, highlightedIndex: 0 })
  }

  select (option) {
    const { multi, onChange } = this.props
    const { isNew, ...value } = option
    onChange(multi ? this.selectedOptions().concat(value) : value)
    if (multi) {
      this.setState({ inputValue: null, highlightedIndex: 0 })
    } else {
      this.close()
    }
  }

  remove (option) {
    this.props.onChange(this.selectedOptions().filter((o) => !sameValue(o, option)))
    this.input.focus()
  }

  clear () {
    this.props.onChange(this.props.multi ? [] : null)
    this.input.focus()
  }

  handleInputChange (event) {
    const inputValue = event.target.value
    this.setState({ inputValue, isOpen: true, highlightedIndex: 0 })
    this.props.onInputChange(inputValue)
  }

  handleKeyDown (event) {
    const { isOpen, highlightedIndex, inputValue } = this.state
    const options = this.visibleOptions()

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (!isOpen) return this.open()
        return this.setState({ highlightedIndex: Math.min(highlightedIndex + 1, options.length - 1) })
      case 'ArrowUp':
        event.preventDefault()
        if (!isOpen) return this.open()
        return this.setState({ highlightedIndex: Math.max(highlightedIndex - 1, 0) })
      case 'Enter':
        if (isOpen && options[highlightedIndex]) {
          event.preventDefault()
          this.select(options[highlightedIndex])
        }
        return
      case 'Escape':
        if (isOpen) {
          // keep an enclosing modal open
          event.stopPropagation()
          this.close()
        }
        return
      case 'Backspace':
        if (this.props.multi && !inputValue) {
          const selected = this.selectedOptions()
          if (selected.length) this.remove(selected[selected.length - 1])
        }
    }
  }

  handleBlur () {
    this.close()
    this.props.onBlur()
  }

  render () {
    const { multi, disabled, isLoading, placeholder, icon, className } = this.props
    const { isOpen, highlightedIndex, inputValue } = this.state
    const selected = this.selectedOptions()
    const options = isOpen ? this.visibleOptions() : []
    const listId = `${this.id}-listbox`
    const ariaName = {
      'aria-labelledby': this.props['aria-labelledby'],
      'aria-label': this.props['aria-label']
    }

    let displayValue = inputValue
    if (displayValue === null) displayValue = !multi && selected[0] ? selected[0].label : ''

    let status = ''
    if (isLoading) status = 'Loading'
    else if (isOpen) status = options.length ? `${options.length} results available` : 'No results found'

    const classes = [
      css.combobox,
      disabled ? css.disabled : null,
      className
    ]

    return (
      <div className={compact(classes).join(' ')}>
        <div className={css.control} onClick={() => { this.input.focus(); this.open() }}>
          <div className={css.values}>
            {multi && selected.map((option) => (
              <span key={option.value} className={css.chip}>
                <button
                  type='button'
                  className={css.chipRemove}
                  aria-label={`Remove ${option.label}`}
                  disabled={disabled}
                  onClick={(e) => { e.stopPropagation(); this.remove(option) }}
                >
                  <span aria-hidden='true'>×</span>
                </button>
                <span className={css.chipLabel}>{option.label}</span>
              </span>
            ))}
            <input
              id={this.id}
              ref={(el) => { this.input = el }}
              className={css.input}
              type='text'
              role='combobox'
              autoComplete='off'
              aria-autocomplete='list'
              aria-expanded={isOpen ? 'true' : 'false'}
              aria-controls={listId}
              aria-activedescendant={isOpen && options[highlightedIndex] ? this.optionId(highlightedIndex) : undefined}
              aria-describedby={this.props['aria-describedby']}
              aria-invalid={this.props['aria-invalid']}
              {...ariaName}
              value={displayValue}
              placeholder={selected.length ? undefined : placeholder}
              disabled={disabled}
              onChange={this.handleInputChange}
              onKeyDown={this.handleKeyDown}
              onBlur={this.handleBlur}
            />
          </div>
          {isLoading && <span className={css.spinner} aria-hidden='true' />}
          {selected.length > 0 && !disabled && (
            <button
              type='button'
              className={css.clear}
              aria-label='Clear selection'
              onClick={(e) => { e.stopPropagation(); this.clear() }}
            >
              <span aria-hidden='true'>×</span>
            </button>
          )}
          {icon || <span className={css.arrow} aria-hidden='true' />}
        </div>
        <ul
          id={listId}
          role='listbox'
          className={isOpen && (options.length || !isLoading) ? css.menu : css.hidden}
          aria-multiselectable={multi ? 'true' : undefined}
          {...ariaName}
        >
          {options.map((option, index) => (
            <li
              key={option.isNew ? '__new__' : option.value}
              id={this.optionId(index)}
              role='option'
              aria-selected={index === highlightedIndex ? 'true' : 'false'}
              className={index === highlightedIndex ? css.highlighted : css.option}
              // keep focus in the input so blur doesn't close the menu first
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => this.setState({ highlightedIndex: index })}
              onClick={() => this.select(option)}
            >
              {option.isNew ? this.props.promptTextCreator(option.label) : option.label}
            </li>
          ))}
          {isOpen && !options.length && !isLoading && (
            <li role='presentation' className={css.noResults}>No results found</li>
          )}
        </ul>
        <div role='status' className={css.visuallyHidden}>{status}</div>
      </div>
    )
  }
}

Combobox.propTypes = {
  id: React.PropTypes.string,
  options: React.PropTypes.arrayOf(React.PropTypes.shape({
    label: React.PropTypes.node,
    value: React.PropTypes.any
  })).isRequired,
  /* An option, or an array of options when `multi` */
  value: React.PropTypes.oneOfType([React.PropTypes.object, React.PropTypes.array]),
  onChange: React.PropTypes.func.isRequired,
  onInputChange: React.PropTypes.func,
  onBlur: React.PropTypes.func,
  multi: React.PropTypes.bool,
  /* Offers a "Create" option for values not in `options` */
  allowCustom: React.PropTypes.bool,
  promptTextCreator: React.PropTypes.func,
  placeholder: React.PropTypes.string,
  disabled: React.PropTypes.bool,
  isLoading: React.PropTypes.bool,
  /* Replaces the dropdown arrow, e.g. a search icon */
  icon: React.PropTypes.node,
  className: React.PropTypes.string,
  'aria-label': React.PropTypes.string,
  'aria-labelledby': React.PropTypes.string,
  'aria-describedby': React.PropTypes.string,
  'aria-invalid': React.PropTypes.bool
}

Combobox.defaultProps = {
  onInputChange: noop,
  onBlur: noop,
  promptTextCreator: (label) => `Create "${label}"`
}
